import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import CloseIcon from '@mui/icons-material/Close'
import LayersClearIcon from '@mui/icons-material/LayersClear'
import { ContentCopy } from '@mui/icons-material'
import {
  AccessLevel,
  SessionRegion,
  useSessionContext
} from 'session'
import { useGlobalMessageHandler } from 'components'

interface SettingsDialogProps {
  open: boolean
  onClose: () => void
}

interface SettingsRowProps {
  label: string
  hint?: string
  children: React.ReactNode
}

const SettingsRow: React.FC<SettingsRowProps> = ({ label, hint, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant='subtitle2'>{label}</Typography>
      {hint != null && (
        <Typography variant='caption' color='text.secondary' component='p'>{hint}</Typography>
      )}
    </Box>
    {children}
  </Box>
)

export const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, onClose }) => {
  const {
    sessionData,
    changeRegion,
    changeNewTab,
    changeKeepDisabledTextBlack,
    openNewTab,
    hasAccessLevel
  } = useSessionContext()
  const { showSuccessMessage } = useGlobalMessageHandler()

  const isLoggedIn = Boolean(sessionData?.username)

  const clearLocalLuts = (): void => {
    localStorage.clear()
    showSuccessMessage('Local LUTs cleared. Reloading...')
    setTimeout(() => { location.reload() }, 2000)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon color='primary' fontSize='small' />
        Settings
        <IconButton size='small' title='Close' sx={{ ml: 'auto' }} onClick={onClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <SettingsRow label='Region' hint='Which dataset you are editing'>
          <ToggleButtonGroup
            exclusive
            size='small'
            color='primary'
            value={sessionData.region}
            onChange={(_event, next: SessionRegion | null) => { if (next !== null) changeRegion(next) }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }
              }
            }}
          >
            <ToggleButton value={SessionRegion.US}>US</ToggleButton>
            <ToggleButton value={SessionRegion.INTL}>International</ToggleButton>
          </ToggleButtonGroup>
        </SettingsRow>
        <SettingsRow label='Open records in new tabs' hint='Edit links open in a separate browser tab'>
          <Switch
            size='small'
            checked={sessionData.newTab}
            onChange={(_event, checked) => { changeNewTab(checked) }}
          />
        </SettingsRow>
        <SettingsRow
          label='Keep text black when disabled'
          hint='Disabled fields stay readable instead of graying out'
        >
          <Switch
            size='small'
            checked={sessionData.keepDisabledTextBlack}
            onChange={(_event, checked) => { changeKeepDisabledTextBlack(checked) }}
          />
        </SettingsRow>
        <SettingsRow label='Delete local LUTs' hint='Clears cached lookup tables on this machine'>
          <Button
            variant='outlined'
            size='small'
            endIcon={<LayersClearIcon />}
            disabled={!isLoggedIn}
            onClick={clearLocalLuts}
          >
            Delete
          </Button>
        </SettingsRow>
        {sessionData.region === SessionRegion.INTL && hasAccessLevel(AccessLevel.MANAGER) && (
          <SettingsRow label='Process duplicates' hint='Opens the duplicate-person merge page'>
            <Button
              variant='outlined'
              size='small'
              endIcon={<ContentCopy />}
              disabled={!isLoggedIn}
              onClick={() => { openNewTab('/duplicates') }}
            >
              Process
            </Button>
          </SettingsRow>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
